import React, { useEffect, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import PhotoViewer from "./PhotoViewer";
import { Dialog } from "../ui/dialog";
import { Button } from "../ui/button";
import RoomPhotoSlider from "./RoomPhotoSlider";
import RoomBookingDialog from "./RoomBookingDialog";
import { photoUrl } from "../../config";

function DesignVariantCard({ room, variant, photo_url, onBook, onPhotoClick }) {
  const [photos, setPhotos] = useState([{ src: "/empty.jpg", name: "" }]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    if (variant?.photos?.length > 0) {
      const list = [];
      for (const ph of variant.photos) {
        list.push({
          src: `${photoUrl}/${photo_url}/numbers/${ph}.jpg`,
          name: ph,
        });
      }
      setPhotos(list);
    }
  }, []);

  const prev = () => {
    setDirection(-1);
    setPhotoIndex(
      (i) => (i - 1 + variant.photos.length) % variant.photos.length,
    );
  };
  const next = () => {
    setDirection(1);
    setPhotoIndex((i) => (i + 1) % variant.photos.length);
  };

  if (!variant.separate) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 flex flex-col">
      <RoomPhotoSlider
        images={photos}
        positions={{}} // room.photos_positions
        currentIndex={photoIndex}
        direction={direction}
        onPrev={prev}
        onNext={next}
        onPhotoClick={(i) => onPhotoClick(photos, i)}
        className="h-48"
      >
        <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm z-10">
          {variant.space} м²
        </div>
      </RoomPhotoSlider>

      {/*  flex-1 */}
      <div className="p-4 flex flex-col">
        <h3 className="text-base font-bold text-gray-900 mb-2">
          {variant.title}
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-1">
          {variant.description}
        </p>
        <Button
          onClick={() => onBook({ ...room, selected_number: variant.id })}
          className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white text-sm h-9 mt-auto"
        >
          Свободные даты
        </Button>
      </div>
    </div>
  );
}

export default function DesignVariantsDialog({
  hotelData,
  room,
  roomPhotoUrl,
  open,
  onOpenChange,
  onBooking,
}) {
  const [bookingRoom, setBookingRoom] = useState(null);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [photoViewerImages, setPhotoViewerImages] = useState([]);
  const [photoViewerIndex, setPhotoViewerIndex] = useState(0);

  const openPhotoViewer = (images, index = 0) => {
    setPhotoViewerImages(images);
    setPhotoViewerIndex(index);
    setPhotoViewerOpen(true);
  };

  const handleBook = (room) => {
    onOpenChange(false);
    setBookingRoom(room);
    setBookingOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content
            className="fixed z-50 w-full max-w-3xl bg-white shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95
            /* Mobile: прижат к верху */
            top-0 left-0 right-0 rounded-b-2xl h-[92vh] flex flex-col
            /* Desktop: по центру */
            sm:top-[50%] sm:left-[50%] sm:right-auto sm:translate-x-[-50%] sm:translate-y-[-50%] sm:rounded-lg sm:h-[85vh]
          "
          >
            {/* Фиксированный заголовок */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-white rounded-t-2xl sm:rounded-t-lg flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-900">
                Варианты дизайна номера
              </h2>
              <DialogPrimitive.Close className="rounded-sm opacity-70 hover:opacity-100 transition-opacity focus:outline-none">
                <X className="h-5 w-5" />
                <span className="sr-only">Закрыть</span>
              </DialogPrimitive.Close>
            </div>
            {/* Скроллируемый контент */}
            <div className="overflow-y-auto flex-1 p-4 sm:p-6 flex flex-col">
              {/*  flex-1 */}
              <div className="grid sm:grid-cols-3 gap-4 pb-4">
                {room?.numbers?.length > 0 &&
                  room.numbers.map((variant) => (
                    <DesignVariantCard
                      key={variant.id}
                      photo_url={roomPhotoUrl}
                      room={room}
                      variant={variant}
                      onBook={handleBook}
                      onPhotoClick={openPhotoViewer}
                    />
                  ))}
              </div>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </Dialog>

      <RoomBookingDialog
        open={bookingOpen}
        onOpenChange={setBookingOpen}
        onBooking={onBooking}
        room={bookingRoom}
        hotelData={hotelData}
      />
      <PhotoViewer
        images={photoViewerImages}
        initialIndex={photoViewerIndex}
        open={photoViewerOpen}
        onOpenChange={setPhotoViewerOpen}
      />
    </>
  );
}
